import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

interface SiteSettings {
  // Branding
  site_title: string;
  site_description?: string;
  logo_url?: string | null;
  favicon_url?: string | null;
  primary_color?: string;
  secondary_color?: string;
  
  // Localization
  timezone: string;
  date_format: string;
  time_format: string;
  default_language: string;
  supported_languages?: string[];
  
  // Contact
  contact_email?: string | null;
  contact_phone?: string | null;
  website_url?: string | null;
  facebook_url?: string | null;
  line_official?: string | null;
  
  [key: string]: any;
}

interface SettingsContextType {
  settings: SiteSettings | null;
  loading: boolean;
  formatDateTime: (date: string | Date, includeTime?: boolean) => string;
  refreshSettings: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [loading, setLoading] = useState(true);

  const API_URL = import.meta.env.VITE_API_URL || 'https://stardust-aims.amy.care';

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/settings/site`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
      // Fallback to default settings
      setSettings({
        site_title: 'Alumni Management System',
        timezone: 'Asia/Bangkok',
        date_format: 'DD/MM/YYYY',
        time_format: 'HH:mm',
        default_language: 'th',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const formatDateTime = (date: string | Date, includeTime: boolean = true): string => {
    if (!date) return '-';
    
    try {
      // Parse the date string - ensure it's treated as UTC if no timezone is specified
      let dateObj: Date;
      if (typeof date === 'string') {
        // If the string doesn't end with 'Z' or timezone offset, append 'Z' to treat as UTC
        const dateStr = date.includes('Z') || date.includes('+') || date.includes('T') && date.split('T')[1]?.includes('-') 
          ? date 
          : date.replace(' ', 'T') + 'Z';
        dateObj = new Date(dateStr);
      } else {
        dateObj = date;
      }
      
      // Check if date is valid
      if (isNaN(dateObj.getTime())) {
        console.error('Invalid date:', date);
        return '-';
      }
      
      // Get timezone from settings, fallback to Asia/Bangkok
      const timezone = settings?.timezone || 'Asia/Bangkok';
      
      // Create options for Intl.DateTimeFormat
      const options: Intl.DateTimeFormatOptions = {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      };

      if (includeTime) {
        options.hour = '2-digit';
        options.minute = '2-digit';
        options.second = '2-digit';
        options.hour12 = false;
      }

      // Use the configured timezone to format the date
      const formatter = new Intl.DateTimeFormat('en-GB', options);
      return formatter.format(dateObj);
    } catch (error) {
      console.error('Error formatting date:', error, 'Input:', date);
      return '-';
    }
  };

  return (
    <SettingsContext.Provider value={{ settings, loading, formatDateTime, refreshSettings: loadSettings }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}
